<template>
    <div v-for="(variantGroup, index) in computedVariants" :key="index" class="form-control mb-8">
        <div v-if="variantGroup.options?.length > 0" class="mb-2 label-text text-lg font-semibold text-headline">
            {{ t('product.detailVariants.orderAs') }}
        </div>

        <div class="flex flex-row flex-wrap gap-2 items-center">
            <div v-for="variantOption in limitedVariants.options" :key="variantOption.id">
                <label
                    class="btn cursor-pointer border-border rounded-lg btn-sm text-lg leading-none py-2 h-auto text-left"
                    :class="{
                        'btn-outline bg-white hover:bg-placeholder-focus hover:text-white hover:border-placeholder-focus text-placeholder-focus': selectedOptions[variantGroup.id] !== variantOption.id,
                        'bg-variant-select text-white hover:bg-variant-select-hover hover:text-placeholder-focus hover:border-variant-select-hover': selectedOptions[variantGroup.id] == variantOption.id,
                        'btn-circle': variantOption.color != null || variantOption.media != null,
                        'btn-disabled': loading
                    }"
                >
                    <div v-if="variantOption.color" class="avatar">
                        <div class="w-8 rounded-full" :style="`background-color: ${variantOption.color};`" />
                    </div>
                    <span v-else-if="variantOption.media" class="avatar">
                        <div class="w-8 rounded-full">
                            <img v-lazy-load  :data-src="variantOption.media.url" :alt="variantOption.media.alt">
                        </div>
                    </span>
                    <span v-else>{{ variantOption.name }}</span>
                    <input
                        v-model="selectedOptions[variantGroup.id]"
                        type="radio"
                        :disabled="loading"
                        :name="variantGroup.id"
                        :value="variantOption.id"
                        class="sr-only radio"
                        @change="onVariantChange(variantGroup.id, variantOption.id)"
                    >
                </label>
            </div>

            <MiscLink
                v-for="(crossSellingOption, index) in limitedVariants.crossSelling"
                :key="index"
                :to="crossSellingOption.seoUrls?.seoPathInfo ? `/` + crossSellingOption.seoUrls?.seoPathInfo : crossSellingOption.seoUrls?.pathInfo"
                class="btn cursor-pointer border-border rounded-lg btn-sm text-lg leading-none py-2 h-auto btn-outline bg-white hover:bg-placeholder-focus hover:text-white hover:border-placeholder-focus text-placeholder-focus max-w-full text-left"
            >

                {{ crossSellingOption.name }}
            </MiscLink>

            <div v-if="variantNumber > 5" class="cursor-pointer" @click="variantToggle = !variantToggle">
                <div v-if="!variantToggle" class="flex flex-row gap-2 text-lg font-semibold">
                    {{ t('product.detailVariants.moreVariants') }}
                    <MiscIcon :name="'chevron-right'" :size="'lg'" />
                </div>
                <div v-else class="flex flex-row gap-2 text-lg font-semibold">
                    {{ t('product.detailVariants.lessVariants') }}
                    <MiscIcon :name="'chevron-left'" :size="'lg'" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue'
import type { HblProduct, HblVariantGroup } from '@/utils/types'
import { useNuxtApp, useNotification, usePage } from '#imports'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
    variants?: HblVariantGroup[],
    defaultOptions?: HblProduct['defaultOptions'],
    parentId?: string,
    variantCrossSelling?: object,
    defaultVariants?: object
}>()

const {
    defaultVariants = [{
        id: "default-group",
        name: "Default Variant Group",
        options: []
    }]
} = props;

const computedVariants = computed(() => props.variants ? props.variants : defaultVariants);

const selectedOptions = ref({})
setDefaultOptions()

function setDefaultOptions () {
    props.variants?.forEach((group) => {
        group.options?.forEach((option) => {
            if (props.defaultOptions?.includes(option.id)) {
                selectedOptions.value[group.id] = option.id
            }
        })
    })
}

const { getProductVariant, loading, error } = usePage()
const { $hblBus } = useNuxtApp()
const { showNotification } = useNotification()
const emit = defineEmits<{(e: 'loading', loading: boolean): void }>()

const variantToggle = ref(false)
const optionsLength = ref(props.variants ? props.variants[0]?.options?.length : 0)
const variantCrossSellingLength = ref(props.variantCrossSelling?.elements?.length)
const variantNumber = ref(variantCrossSellingLength?.value + optionsLength?.value)

const limitedVariants = computed(() => {
    const options = props.variants && props.variants.length > 0 ? props.variants[0]?.options || [] : []
    const crossSelling = props.variantCrossSelling?.elements || [];

    if (variantToggle.value) {
        return {
            options: options,
            crossSelling: crossSelling
        };
    }

    const remainingSlots = 5;
    const optionsLimit = Math.min(options?.length, remainingSlots);
    const crossSellingLimit = Math.min(crossSelling?.length, remainingSlots - optionsLimit);

    return {
        options: options.slice(0, optionsLimit),
        crossSelling: crossSelling.slice(0, crossSellingLimit)
    };
});

async function onVariantChange (switchedOption: string, switchedGroup: string) {
    emit('loading', true);

    try {
        const variant = await getProductVariant(props.parentId, selectedOptions.value, switchedOption, switchedGroup);
        emit('loading', false);

        if (error.value) {
            console.log(error.value)
            showNotification(error.value.toString(), 'error', true)
            return
        }

        localStorage.setItem(
            'selectedVariant',
            JSON.stringify({
                parentId: props.parentId,
                options: selectedOptions.value,
                variantId: variant.id,
            })
        );

        $hblBus.$emit('productVariantChanged', { data: variant });
    } catch (e) {
        emit('loading', false);
        console.error(e);
        showNotification('Error fetching variant', 'error', true);
    }
}

function initializeStoredVariant() {
    const storedVariant = localStorage.getItem('selectedVariant');

    if (storedVariant) {
        try {
            const parsedVariant = JSON.parse(storedVariant);

            if (parsedVariant.parentId === props.parentId) {
                selectedOptions.value = parsedVariant.options;
                onVariantChange('', '');
            }
        } catch (e) {
            console.error('Error parsing stored variant:', e);
        }
    }
}

onMounted(() => {
    initializeStoredVariant();
});
</script>
